import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame, Trophy, Clock, AlertTriangle, Crown, History, Gift, LifeBuoy, Bell,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { parentDashboard, api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PastDueBanner from "@/components/PastDueBanner";
import BillingPortalButton from "@/components/BillingPortalButton";
import RefundRequestModal from "@/components/RefundRequestModal";
import PageHeader from "@/components/PageHeader";
import { formatMinutes } from "@/lib/time";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function WeeklyChart({ perDay, weekDays, childName }) {
  const days = (weekDays || []).map((iso, i) => {
    const mins = Math.round((perDay?.[iso] || 0) / 60);
    const [, m, d] = iso.split("-");
    return { iso, mins, label: DAY_LABELS[i], short: `${parseInt(d, 10)}/${parseInt(m, 10)}` };
  });
  const max = Math.max(1, ...days.map((d) => d.mins));
  const hasAny = days.some((d) => d.mins > 0);
  // Screen reader summary
  const srSummary = `Study time this week for ${childName}: ` +
    days.map((d) => `${d.label} ${formatMinutes(d.mins)}`).join(", ") + ".";

  return (
    <div>
      <span className="sr-only">{srSummary}</span>
      <div className="flex items-end gap-2 h-24" aria-hidden="true">
        {days.map((d) => (
          <div key={d.iso} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div
              className={`w-full border-2 border-black dark:border-white rounded-sm ${d.mins > 0 ? "bg-[#D4C4FB] dark:bg-[#B794F4]" : "bg-transparent border-dashed"}`}
              style={{ height: `${(d.mins / max) * 80}px`, minHeight: d.mins > 0 ? 6 : 4 }}
              title={`${d.label} ${formatMinutes(d.mins)}`}
            />
            <div className="text-[11px] font-bold tabular-nums text-muted leading-none">{d.label}</div>
            <div className="text-[10px] font-medium tabular-nums text-muted leading-none">{d.short}</div>
          </div>
        ))}
      </div>
      {!hasAny && (
        <div className="text-sm font-bold text-muted mt-3">No study logged this week.</div>
      )}
    </div>
  );
}

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundOpen, setRefundOpen] = useState(false);

  useEffect(() => {
    parentDashboard().then(setData).finally(() => setLoading(false));
  }, []);

  const hasBilling = user?.stripe_customer_id && ["active", "trialing", "past_due", "canceled"].includes(user?.subscription_status);
  const isPremium = ["active", "trialing"].includes(user?.subscription_status);

  const sendReminder = async (child) => {
    try {
      await api.post("/parent/send-reminder", { child_id: child.id });
      toast.success(`Nudge sent to ${child.name}`);
    } catch (e) {
      toast.error("Could not send reminder");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950">
      <PastDueBanner />
      <PageHeader
        backTo="/family"
        backLabel="Profiles"
        title="Parent dashboard"
        actions={
          <>
            <button
              onClick={() => navigate("/parent/exam-history")}
              data-testid="parent-exam-history-btn"
              className="nb-button px-3 py-2 text-sm hidden sm:flex items-center gap-1"
            >
              <History className="w-4 h-4" aria-hidden="true" /> Exam history
            </button>
            {isPremium && (
              <div className="nb-border rounded-full px-3 py-1.5 bg-[#FFE066] hidden md:flex items-center gap-1 text-xs font-bold text-black" aria-label="Premium plan">
                <Crown className="w-3.5 h-3.5" aria-hidden="true" /> Premium
              </div>
            )}
            {["active", "trialing", "past_due"].includes(user?.subscription_status) ? (
              <BillingPortalButton returnPath="/parent" className="nb-button px-3 py-2 text-sm" testId="parent-billing-portal">
                Billing
              </BillingPortalButton>
            ) : (
              <button onClick={() => navigate("/pricing")} className="nb-button-primary px-3 py-2 text-sm" data-testid="parent-upgrade">Upgrade</button>
            )}
            {hasBilling && (
              <button
                onClick={() => setRefundOpen(true)}
                data-testid="parent-refund-btn"
                className="nb-button px-3 py-2 text-sm hidden sm:flex items-center gap-1"
                title="Request a refund"
              >
                <LifeBuoy className="w-4 h-4" aria-hidden="true" /> Refund
              </button>
            )}
          </>
        }
      />

      <main className="max-w-[960px] mx-auto p-6 sm:p-10" data-testid="parent-dashboard">
        {loading ? (
          <div className="font-bold text-muted">Loading…</div>
        ) : !data?.length ? (
          <div className="nb-card p-8 text-center">
            <h2 className="font-display text-2xl font-extrabold mb-2">No children added yet</h2>
            <button onClick={() => navigate("/family")} className="nb-button-primary px-5 py-3">Add child</button>
          </div>
        ) : (
          <div className="space-y-10">
            {data.map((row, i) => {
              const minutes = row.total_minutes_week || 0;
              const hasActivity = minutes > 0 || (row.recent_attempts?.length || 0) > 0;
              const weakCount = row.weak_topics?.length || 0;
              return (
                <motion.section
                  key={row.child.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  aria-labelledby={`child-${row.child.id}-heading`}
                >
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      <img
                        src={`https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(row.child.avatar_seed || row.child.name)}`}
                        alt={`${row.child.name}'s avatar`}
                        className="w-16 h-16 nb-border rounded-2xl bg-[#D4C4FB]"
                      />
                      <div>
                        <h2 id={`child-${row.child.id}-heading`} className="font-display text-3xl font-extrabold tracking-tight">{row.child.name}</h2>
                        <div className="text-sm font-bold text-muted">Year {row.child.year_group}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <div className="nb-border rounded-xl px-3 py-2 bg-[#FFB38E] flex items-center gap-2 text-black font-bold text-sm" aria-label={`${row.child.streak || 0} day streak`} data-testid="streak-pill">
                        <Flame className="w-4 h-4" aria-hidden="true" /> <span className="tabular-nums">{row.child.streak || 0}d</span> streak
                      </div>
                      <div className="nb-border rounded-xl px-3 py-2 bg-[#FFE066] flex items-center gap-2 text-black font-bold text-sm" aria-label={`${row.child.xp || 0} XP`}>
                        <Trophy className="w-4 h-4" aria-hidden="true" /> <span className="tabular-nums">{row.child.xp || 0}</span> XP
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-5">
                    {/* This week, static container, no shadow */}
                    <div className="nb-container p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4" aria-hidden="true" />
                        <h3 className="label-caps">This week</h3>
                      </div>
                      <div className="tabular-nums text-4xl font-black mb-3" aria-label={`${formatMinutes(minutes)} studied this week`}>
                        {formatMinutes(minutes)}
                      </div>
                      <WeeklyChart perDay={row.per_day_seconds} weekDays={row.week_days} childName={row.child.name} />
                      {!hasActivity && (
                        <div className="mt-4 pt-4 nb-border border-l-0 border-r-0 border-b-0">
                          <div className="text-sm font-bold mb-2">
                            {row.child.name} hasn't started this week yet.
                          </div>
                          <button
                            onClick={() => sendReminder(row.child)}
                            data-testid={`send-reminder-${row.child.id}`}
                            className="nb-button px-3 py-2 text-sm flex items-center gap-2"
                          >
                            <Bell className="w-4 h-4" aria-hidden="true" /> Send {row.child.name} a reminder
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Mastery, static container */}
                    <div className="nb-container p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy className="w-4 h-4" aria-hidden="true" />
                        <h3 className="label-caps">Mastery</h3>
                      </div>
                      <div className="space-y-2 text-sm font-bold">
                        <div className="flex justify-between">
                          <span>Mastered (≥80%)</span>
                          <span className="tabular-nums">{row.mastered}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>In progress</span>
                          <span className="tabular-nums">{row.in_progress}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Weak spots</span>
                          <span className={`tabular-nums ${weakCount > 0 ? "text-[#C2410C] dark:text-[#F6AD55]" : ""}`}>{weakCount}</span>
                        </div>
                      </div>
                    </div>

                    {/* Needs work, static container */}
                    <div className="nb-container p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                        <h3 className="label-caps">Needs work</h3>
                      </div>
                      {weakCount > 0 ? (
                        <ul className="space-y-1.5">
                          {row.weak_topics.slice(0, 4).map((w) => (
                            <li key={w.topic_id} className="text-sm font-bold">
                              <span className="text-muted mr-2">{w.subject}</span>
                              {w.topic_name}
                              <span className="ml-2 text-xs nb-border rounded-full px-2 py-0.5 bg-[#FFB38E] text-black tabular-nums">{w.mastery}%</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-sm font-medium text-muted">
                          {hasActivity ? `No weak spots so far. Nice work, ${row.child.name}.` : `Will appear after ${row.child.name}'s first quiz.`}
                        </div>
                      )}
                    </div>
                  </div>

                  {row.recent_attempts?.length > 0 && (
                    <div className="nb-container p-5 mt-5">
                      <h3 className="label-caps mb-3">Recent quizzes</h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {row.recent_attempts.map((a) => (
                          <div key={a.id} className="nb-border rounded-xl p-3 bg-zinc-50 dark:bg-zinc-800 flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-muted">{a.subject}</div>
                              <div className="font-bold text-sm truncate">{a.topic_name}</div>
                            </div>
                            <div className={`tabular-nums text-2xl font-black ${a.score_pct >= 80 ? "text-green-700" : a.score_pct >= 60 ? "text-yellow-700" : "text-[#C2410C]"}`}>
                              {a.score_pct}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.section>
              );
            })}

            {/* Refer card, moved out of the header per the design pass. */}
            <div className="nb-card p-5 bg-[#FFE066] text-black">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 label-caps text-black">
                    <Gift className="w-4 h-4" aria-hidden="true" /> Refer a parent
                  </div>
                  <p className="font-bold text-sm mt-1">Earn £5 for every friend who subscribes.</p>
                </div>
                <button onClick={() => navigate("/refer")} className="nb-button px-4 py-2 text-sm" data-testid="parent-refer-card-btn">
                  Share your code
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <RefundRequestModal open={refundOpen} onClose={() => setRefundOpen(false)} />
    </div>
  );
}
